/*
 * Jira Ticket:
 * Created Date: Mon, 1st Jun 2020, 17:16:27 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import {
  Create,
  DateInput,
  ImageField,
  ImageInput,
  NumberInput,
  required,
  ReferenceArrayInput,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useNotify,
  useTranslate,
  useQuery,
  usePermissions,
  Title,
} from 'react-admin';
import { InputAdornment } from '@material-ui/core';
import { DateInput as ReactAdminDateInput } from 'react-admin-date-inputs';
import { useForm } from 'react-final-form';
import { AccessDenied } from 'Layout';
import { columnStyles } from 'styles';
import {
  isValidUrl,
  nonNegativeNonZeroInt,
  validateLiveDate,
  validateEndDate,
} from 'utils/validation';
import { isSuperAdmin } from 'utils/roles';
import { createAdStatusOptions } from 'utils/choices';
import { onDropRejected, onDrop } from 'utils';

const maxFileSize = 10485760; // 10MB in bytes
const allowedFileTypes = 'image/jpg, image/jpeg, image/png';
const aspectRatio = 0.67;
const configId = '00000000-0000-0000-0000-000000000000';

const liveDateValidation = [required(), validateLiveDate];
const endDateValidation = [required(), validateEndDate];
const urlValidation = [required(), isValidUrl];
const freqWeightValidation = [required(), nonNegativeNonZeroInt];

const AspectRatioValidatingImageField = ({
  imgLoaded,
  resetImageField,
  ...props
}) => {
  const {
    record: { rawFile },
  } = props;
  var calcAspectRatio = null;
  if (rawFile.width && rawFile.height) {
    calcAspectRatio =
      Math.round(
        (Number(rawFile.width / rawFile.height) + Number.EPSILON) * 100
      ) / 100;
  }
  useEffect(() => {
    if (calcAspectRatio && calcAspectRatio !== aspectRatio) {
      resetImageField();
    }
  }, [resetImageField, calcAspectRatio]);

  return imgLoaded && calcAspectRatio && calcAspectRatio === aspectRatio ? (
    <ImageField source="undefined" title="img" {...props} />
  ) : null;
};

const SanitizedForm = ({
  classes,
  notify,
  urlValidation,
  freqWeightValidation,
  duration,
  translate,
  setImgLoaded,
  imgLoaded,
}) => {
  const form = useForm();
  const resetImageField = () => {
    // reset the adImage field if the aspect ratio is not correct
    notify('error.advert.wrongAspectRatio', 'warning');
    return form.change('adImage', null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          label="resources.Advert.fields.adName"
          validate={required()}
        />
        <TextInput
          source="advertiserName"
          label="resources.Advert.fields.advertiserName"
          validate={required()}
        />
        <TextInput
          source="url"
          label="resources.Advert.fields.url"
          type="url"
          validate={urlValidation}
        />
        <ReferenceArrayInput
          label="resources.Advert.fields.regions"
          source="regions"
          resource="Advert"
          reference="Region"
          perPage={1000}
          validate={required()}
          sort={{ field: 'name', order: 'ASC' }}
        >
          <SelectArrayInput
            source="id"
            optionText="name"
            label="resources.Advert.fields.regionsRequired"
          />
        </ReferenceArrayInput>
        {duration && (
          <NumberInput
            source="duration"
            InputProps={{
              endAdornment: <InputAdornment position="end">ms</InputAdornment>,
            }}
            fullWidth
            defaultValue={duration}
            initialValue={duration}
            validate={required()}
            label={'resources.Advert.fields.duration'}
          />
        )}
        <NumberInput
          source="frequencyWeight"
          defaultValue={1}
          validate={freqWeightValidation}
          label="resources.Advert.fields.freqWeight"
        />
        <SelectInput
          source="status"
          choices={createAdStatusOptions}
          validate={required()}
          defaultValue={'DRAFT'}
          label="resources.Advert.fields.status"
        />
      </div>
      <div className={classes.extendedColumn}>
        <ImageInput
          source="adImage"
          label={'resources.Advert.fields.media'}
          maxSize={maxFileSize}
          accept={allowedFileTypes}
          fullWidth
          onChange={() => setImgLoaded(false)}
          options={{
            onDropRejected: files =>
              onDropRejected({
                files,
                translate,
                notify,
                maxFileSize,
                allowedFileTypes,
              }),
            onDrop: acceptedFiles => onDrop({ acceptedFiles, setImgLoaded }),
          }}
          validate={required()}
        >
          <AspectRatioValidatingImageField
            imgLoaded={imgLoaded}
            resetImageField={resetImageField}
          />
        </ImageInput>
        <div className={classes.column}>
          {window.navigator.userAgent.indexOf('Safari') > -1 &&
          window.navigator.userAgent.indexOf('Chrome') === -1 ? (
            <ReactAdminDateInput
              source="startDate"
              label="resources.Advert.fields.liveDate"
              alwaysOn
              validate={liveDateValidation}
              options={{
                format: 'dd/MM/yyyy',
                clearable: true,
              }}
            />
          ) : (
            <DateInput
              source="startDate"
              label="resources.Advert.fields.liveDate"
              validate={liveDateValidation}
            />
          )}
          {window.navigator.userAgent.indexOf('Safari') > -1 &&
          window.navigator.userAgent.indexOf('Chrome') === -1 ? (
            <ReactAdminDateInput
              source="endDate"
              label="resources.Advert.fields.endDate"
              alwaysOn
              validate={endDateValidation}
              options={{
                format: 'dd/MM/yyyy',
                clearable: true,
              }}
            />
          ) : (
            <DateInput
              source="endDate"
              label="resources.Advert.fields.endDate"
              validate={endDateValidation}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AdvertCreate = props => {
  const { permissions } = usePermissions();
  const classes = columnStyles();
  const notify = useNotify();
  const translate = useTranslate();
  const [imgLoaded, setImgLoaded] = useState(false); // this notifies us to rerender the ImageField as the onDrop function is synchronous

  const { loaded, error, data } = useQuery({
    type: 'getOne',
    resource: 'Configuration',
    payload: { id: configId },
  });

  if (loaded && error) {
    notify('error.advert.unableToGetConfig', 'warning');
  }

  const rest = {
    classes,
    notify,
    translate,
    urlValidation,
    freqWeightValidation,
    duration: data && data.defaultAdvertLength,
    imgLoaded,
    setImgLoaded,
    ...props,
  };
  return isSuperAdmin(permissions) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Create {...props}>
        <SimpleForm>
          <SanitizedForm {...rest} />
        </SimpleForm>
      </Create>
    </>
  ) : (
    <AccessDenied />
  );
};

export default AdvertCreate;
